import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const _cache = {};

/**
 * usePermission — checks if the current user has a specific granular permission.
 * Returns { allowed: boolean, loading: boolean }.
 * Caches results per-key for the session to avoid repeated API calls.
 */
export const usePermission = (key) => {
  const [allowed, setAllowed] = useState(_cache[key]?.allowed ?? null);
  const [loading, setLoading] = useState(_cache[key] == null);

  useEffect(() => {
    if (!key) { setAllowed(false); setLoading(false); return; }
    if (_cache[key] != null) { setAllowed(_cache[key].allowed); setLoading(false); return; }
    let live = true;
    api.get(`/rbac/check/${key}`)
      .then((r) => {
        if (!live) return;
        const result = !!r.data?.has_permission;
        _cache[key] = { allowed: result };
        setAllowed(result);
      })
      .catch(() => { if (live) setAllowed(false); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [key]);

  return { allowed, loading };
};

/** Clear the cache (e.g. after user permissions change). */
export const clearPermissionCache = () => {
  Object.keys(_cache).forEach((k) => delete _cache[k]);
};

/** Batch check — used by admin panels to pre-load multiple keys. */
export const usePermissions = (keys) => {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!keys || keys.length === 0) { setLoading(false); return; }
    const uncached = keys.filter((k) => _cache[k] == null);
    if (uncached.length === 0) {
      setResults(Object.fromEntries(keys.map((k) => [k, _cache[k].allowed])));
      setLoading(false);
      return;
    }
    // Fetch all uncached in parallel
    Promise.allSettled(uncached.map((k) => api.get(`/rbac/check/${k}`).then((r) => ({ key: k, allowed: !!r.data?.has_permission }))))
      .then((settled) => {
        settled.forEach((s) => {
          if (s.status === 'fulfilled') _cache[s.value.key] = { allowed: s.value.allowed };
        });
        setResults(Object.fromEntries(keys.map((k) => [k, _cache[k]?.allowed ?? false])));
        setLoading(false);
      });
  }, [JSON.stringify(keys)]);

  return { results, loading };
};
